import { _ } from "../../core/polyglot/polyglot.js";
import { formatForceTwoDecimals } from "../../core/helpers/count-formatting-util.js";
import { makeMonitorRepository } from "../../core/repositories/repositoryBuilders.js";
import accountingDocument from "../shared/accountingDocument.js";
import GridColumnSpecification from "../../core/widgets/list/GridColumnSpecification.js";
import InvoiceReportData from "../../models/accounting/invoices/InvoiceReportData.js";
import ListSource from "../../core/list/ListSource.js";
import makeTableListWidget from "../../core/widgets/list/TableListWidget/makeTableListWidget.js";
import ODataStream from "../../core/list/streams/ODataStream.js";
import session from "../../core/session.js";

/**
 * Displays receivable and payable `invoiceGroup`.
 *
 * @abstract
 */
export default accountingDocument.abstractSubclass((that, my) => {
	my.initialize = function () {
		my.super(...arguments);

		my.company = session.getCompany();
		my.repository = my.getRepository();
	};

	my.initializeSubwidgets = function () {
		my.super(...arguments);

		that.help = my.buildHelp();
	};

	that.show = async function () {
		let listSource;

		// The backend always responds with 1 report containing an
		// array of invoices. Its $orderby query parameter sorts the
		// included array. The ListSource we have on the client needs
		// to be connected to a stream of invoices, not to 1 report
		// with many invoices. The code below is more complicated than
		// usual to workaround this weird API.
		const invoiceReportPromise = new Promise((resolve) => {
			listSource = ListSource.makeAndFetch({
				stream: new ODataStream({
					fetcher: ({ filter }) =>
						my.repository.get({ filter }).then((report) => {
							resolve(report);
							return report.getRowData();
						}),
				}),
				sortFieldAccessor: "Reference",
			});
		});

		const invoiceReportData = await invoiceReportPromise;

		my.invoiceReportWidget = makeTableListWidget({
			columnSpecifications: my.getColumnSpec(),
			tfootRenderer: () => invoiceReportData.getSummaryRowData(),
			source: listSource,
			striped: true,
		});

		that.update();
	};

	that.hasActionsMenu = function () {
		return false;
	};

	that.hasPeriodPicker = function () {
		return false;
	};

	my.renderContentOn = function (html) {
		if (!my.invoiceReportWidget) return;

		html.render(
			my.section({
				content: html.div(
					{ klass: "responsive-table invoice-report" },
					my.invoiceReportWidget,
				),
			}),
		);
	};

	my.getRepository = function () {
		return makeMonitorRepository({
			route: my.getRepositoryRoute(),
			model: InvoiceReportData,
			defaultFilters: {
				companyId: my.company.getLegacyId(),
				reportId: my.getReportId(),
			},
			antiForgeryToken: session.getAntiForgeryToken(),
		});
	};

	my.getRepositoryRoute = function () {
		return "/Accounting2/Company/#companyId/InvoiceReport/Aggregate";
	};

	my.getReportId = function () {
		return my.subclassResponsibility();
	};

	my.getColumnSpec = function () {
		return [
			new GridColumnSpecification({
				name: my.getFirstColumnName(),
				sortCriterion: "Reference",
				type: "text",
				dataTestName: "reference",
				renderer: (model) => model.reference,
			}),
			my.makeGridColumnSpecification({
				name: _("On time"),
				columnLabel: "No overdue",
			}),
			my.makeGridColumnSpecification({
				name: _("Day 0-30"),
				columnLabel: "Day 1-30",
			}),
			my.makeGridColumnSpecification({
				name: _("Day 31-60"),
				columnLabel: "Day 31-60",
			}),
			my.makeGridColumnSpecification({
				name: _("Day 61-90"),
				columnLabel: "Day 61-90",
			}),
			my.makeGridColumnSpecification({
				name: _("Day 91-120"),
				columnLabel: "Day 91-120",
			}),
			my.makeGridColumnSpecification({
				name: _("Day 121-365"),
				columnLabel: "Day 121-365",
			}),
			my.makeGridColumnSpecification({
				name: _("Day >365"),
				columnLabel: "Day >365",
			}),
			my.makeGridColumnSpecification({
				name: _("Total"),
				columnLabel: "Total",
				headerCssClass: "text-right",
			}),
		];
	};

	my.makeGridColumnSpecification = function ({
		name,
		columnLabel,
		headerCssClass,
	}) {
		return new GridColumnSpecification({
			name,
			sortCriterion: columnLabel,
			type: "number",
			cssClass: "text-right",
			headerCssClass,
			renderer: (model) => formatForceTwoDecimals(model[columnLabel]),
		});
	};

	my.getHelpMessage = function () {
		return my.subclassResponsibility();
	};

	my.buildHelp = function () {
		return {
			summary: my.getHelpMessage(),
		};
	};
});
